import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CurrentProjectManagerContext } from '../../../contexts/CurrentProjectManagerContext'
import axiosService from '../../../utils/AxiosService'
import Button from '../../Shared/Button'
import Pagination from '../../Shared/Pagination'
import PotentialInfluencerItem from './PotentialInfluencerItem'
import { TABLE_COLUMNS } from './potentialInfluencerConstants'

const EMPTY_META = { total_count: 0, total_pages: 0, per_page: 50 }

export default function PotentialInfluencersTable() {
  const { currentProjectManager } = useContext(CurrentProjectManagerContext)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [potentialInfluencers, setPotentialInfluencers] = useState([])
  const [meta, setMeta] = useState(EMPTY_META)
  const [loading, setLoading] = useState(true)

  const showHidden = searchParams.get('hidden') === 'true'
  const page = Math.max(parseInt(searchParams.get('page'), 10) || 1, 1)

  const updateParams = ({ hidden = showHidden, page: nextPage = page }) => {
    const params = {}
    if (hidden) params.hidden = 'true'
    if (nextPage > 1) params.page = String(nextPage)
    setSearchParams(params)
  }

  useEffect(() => {
    if(!currentProjectManager?.id) return
    if(!currentProjectManager.is_admin) navigate('/project_manager/campaigns')

    getPotentialInfluencers()
  },[currentProjectManager?.id, showHidden, page])

  const getPotentialInfluencers = () => {
    setLoading(true)
    axiosService.get('/api/project_manager/potential_influencers', { params: { hidden: showHidden, page } })
    .then(({ potential_influencers, ...pagination }) => {
      if (pagination.total_pages > 0 && page > pagination.total_pages) {
        updateParams({ page: pagination.total_pages })
        return
      }
      setPotentialInfluencers(potential_influencers)
      setMeta(pagination)
    })
    .catch(resp => console.log(resp))
    .finally(() => setLoading(false))
  }

  const changePage = nextPage => {
    updateParams({ page: nextPage })
    window.scrollTo({ top: 0 })
  }

  const pagination = (
    <Pagination
      page={page}
      totalPages={meta.total_pages}
      totalCount={meta.total_count}
      perPage={meta.per_page}
      onChange={changePage}
      itemLabel="potential influencers"
    />
  )

  return (
    <div className="pt-12 bg-white dark:bg-rb-dark-mode min-h-screen">
      <div className="flex items-center justify-between w-full h-20 px-24 align-center bg-gradient-to-br from-rb-green from-30% via-teal-500 via-60% to-rb-green to-90%">
        <p className="text-white text-2xl">
          Potential Influencer Dashboard
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-8 pb-12">
        <div className="flex gap-2 mb-2">
          <Button variant={!showHidden ? 'primary' : 'secondary'} size="sm" onClick={() => updateParams({ hidden: false, page: 1 })}>
            Active
          </Button>
          <Button variant={showHidden ? 'primary' : 'secondary'} size="sm" onClick={() => updateParams({ hidden: true, page: 1 })}>
            Hidden
          </Button>
        </div>

        {pagination}

        <div className={`rounded-lg border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter overflow-hidden transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <div className="flex items-center gap-4 px-4 py-2 bg-rb-grey dark:bg-rb-dark-mode-lighter text-xs font-semibold uppercase text-rb-mat-grey dark:text-rb-dark-mode-text-grey">
            <span className={TABLE_COLUMNS.id}>Id</span>
            <span className={TABLE_COLUMNS.name}>Name</span>
            <span className={TABLE_COLUMNS.email}>Email</span>
            <span className={TABLE_COLUMNS.created}>Created</span>
            <span className={TABLE_COLUMNS.influencerCreated}>Influencer created</span>
            <span className={TABLE_COLUMNS.influencerCreatedAt}>Created at</span>
            <span className={TABLE_COLUMNS.actions}>Options</span>
          </div>

          {!loading && potentialInfluencers.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-rb-mat-grey dark:text-rb-dark-mode-text-grey">
              {showHidden ? 'No hidden potential influencers.' : 'No potential influencers yet.'}
            </p>
          )}

          {potentialInfluencers.map(potentialInfluencer => <PotentialInfluencerItem key={potentialInfluencer.id} potentialInfluencer={potentialInfluencer} getPotentialInfluencers={getPotentialInfluencers} />)}
        </div>

        {pagination}
      </div>
    </div>
  )
}
